import React from 'react'
import Image from 'next/image'
import { s3Static } from '../../../../utils/constants'
import { motion } from 'framer-motion'
import { FaStar, FaQuoteLeft, FaTrophy, FaDumbbell } from 'react-icons/fa'
import { HiLightningBolt } from 'react-icons/hi'
import Link from 'next/link'

export const ClientStoryAgatha = () => {
    return (
        <section className="py-12 bg-linear-to-b from-gray-50 to-white">
            <div className="max-w-6xl mx-auto px-4">
                {/* Section Header */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center mb-10"
                >
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <FaTrophy className="text-emerald-500 text-2xl" />
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                            Strength Training Success Story
                        </h2>
                    </div>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        How Agatha transformed from beginner to confident strength athlete with expert female coaching
                    </p>
                </motion.div>

                {/* Compact Story Card */}
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    viewport={{ once: true }}
                    className="grid md:grid-cols-2 gap-8 bg-linear-to-br from-emerald-50 to-teal-50 rounded-3xl p-6 md:p-8 shadow-xl border border-emerald-100"
                >
                    {/* Left: Image, Stats & Client Info */}
                    <div className="space-y-4">
                        <div className="relative rounded-2xl overflow-hidden shadow-lg h-96 md:h-120">
                            <Image
                                src={s3Static + "personal-trainer/generated/serene-training-agatha.png"}
                                fill
                                style={{ objectFit: 'cover', objectPosition: 'top' }}
                                alt="Agatha training with Personal Trainer Serene Yeo in Singapore"
                                sizes="(max-width: 768px) 100vw, 50vw"
                            />
                            {/* Floating Badge */}
                            <div className="absolute top-4 left-4 bg-white rounded-full px-3 py-1 shadow-lg flex items-center gap-2">
                                <FaDumbbell className="text-emerald-600" />
                                <span className="font-bold text-sm text-gray-800">Strength Training</span>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white rounded-xl p-3 text-center shadow-md">
                                <div className="text-2xl font-bold text-emerald-600">+6kg</div>
                                <div className="text-xs text-gray-600">Muscle Gain</div>
                            </div>
                            <div className="bg-white rounded-xl p-3 text-center shadow-md">
                                <div className="text-2xl font-bold text-teal-600">6 Months</div>
                                <div className="text-xs text-gray-600">Duration</div>
                            </div>
                        </div>
                        
                    </div>

                    {/* Right: Story Content */}
                    <div className="space-y-4">
                        {/* Client Info */}
                        <div className="bg-white rounded-xl p-4 shadow-md">
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">Agatha Morrison</h3>
                                    <p className="text-sm text-gray-600">Marketing Director, 45 years old</p>
                                    <p className="text-xs text-gray-500 mt-1">📍 Training Location: Condo Gym, River Valley</p>
                                </div>
                                <div className="flex gap-1">
                                    {[...Array(5)].map((_, i) => (
                                        <FaStar key={i} className="text-amber-400 text-sm" />
                                    ))}
                                </div>
                            </div>
                            
                            {/* Quote */}
                            <div className="relative bg-emerald-50 rounded-lg p-3">
                                <FaQuoteLeft className="text-emerald-200 text-xl absolute top-2 left-2 opacity-50" />
                                <p className="text-sm text-gray-700 italic pl-6 leading-relaxed">
                                    &quot;Serene showed me that women can be strong at any age. At 45, I&apos;m lifting heavier than I imagined possible. She made the gym my sanctuary, not a scary place!&quot;
                                </p>
                            </div>
                        </div>
                        {/* Journey Summary */}
                        <div className="bg-white rounded-xl p-4 shadow-md space-y-3">
                            <h4 className="font-bold text-gray-900 flex items-center gap-2">
                                <HiLightningBolt className="text-yellow-500" />
                                Transformation Journey
                            </h4>
                            <div className="space-y-2 text-sm text-gray-700">
                                <p>
                                    <strong>Challenge:</strong> Agatha felt intimidated by gym equipment and believed strength training was only for young men. Wanted to build confidence and muscle mass.
                                </p>
                                <p>
                                    <strong>Solution:</strong> Serene created a progressive strength program, starting with bodyweight and gradually introducing barbells, dumbbells, and resistance training techniques.
                                </p>
                                <p>
                                    <strong>Results:</strong> Gained 6kg lean muscle, increased bone density, improved posture, and now confidently squats 80kg. Feels stronger than in her 20s.
                                </p>
                            </div>
                        </div>

                        {/* Trainer Info */}
                        <div className="bg-linear-to-r from-emerald-600 to-teal-600 rounded-xl p-4 shadow-md text-white">
                            <div className="flex items-center gap-3">
                                <div className="relative w-16 h-16 rounded-full overflow-hidden border-3 border-white shadow-lg shrink-0">
                                    <Image
                                        src={s3Static + "personal-trainer/generated/Serene Yeo.jpeg"}
                                        fill
                                        style={{ objectFit: 'cover' }}
                                        alt="Personal Trainer Serene Yeo"
                                        sizes="64px"
                                    />
                                </div>
                                <div className="flex-grow">
                                    <h5 className="font-bold text-lg">Serene Yeo</h5>
                                    <p className="text-emerald-100 text-xs">NASM Certified (CPT)</p>
                                    <p className="text-emerald-100 text-xs">Female Strength Specialist</p>
                                    <div className="flex gap-1 mt-1">
                                        {[...Array(5)].map((_, i) => (
                                            <FaStar key={i} className="text-yellow-300 text-xs" />
                                        ))}
                                        <span className="ml-1 text-xs">4.9/5.0</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Key Benefits */}
                        <div className="bg-rose-50 rounded-xl p-4 shadow-md">
                            <h4 className="font-bold text-gray-900 mb-2 text-sm">Female Strength Training Benefits:</h4>
                            <ul className="space-y-1 text-xs text-gray-700">
                                <li className="flex items-start gap-2">
                                    <span className="text-emerald-500 mt-0.5">✓</span>
                                    <span>Increased bone density - crucial for women over 40</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-emerald-500 mt-0.5">✓</span>
                                    <span>Boosted metabolism and improved body composition</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-emerald-500 mt-0.5">✓</span>
                                    <span>Female trainer creates safe, judgment-free space</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-emerald-500 mt-0.5">✓</span>
                                    <span>Build confidence and dispel &quot;bulky muscle&quot; myths</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </motion.div>

                {/* CTA */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    viewport={{ once: true }}
                    className="text-center mt-8"
                >
                    <Link 
                        href="/sg/personal-trainer"
                        className="inline-block bg-linear-to-r from-emerald-600 to-teal-600 text-white font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    >
                        Start Your Strength Journey →
                    </Link>
                    <p className="text-sm text-gray-600 mt-3">
                        Build strength, confidence, and transform your body at any age
                    </p>
                </motion.div>

                {/* YMYL Disclaimer */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    viewport={{ once: true }}
                    className="mt-8 bg-gray-50 rounded-xl p-4 border-l-4 border-emerald-500"
                >
                    <p className="text-xs text-gray-700 leading-relaxed">
                        <strong>Disclaimer:</strong> Individual results vary. Agatha&apos;s success resulted from dedicated strength training with a certified trainer, proper nutrition with adequate protein intake, progressive overload principles, and consistent effort over 6 months. StringsSG trainers are qualified fitness professionals, not medical doctors. Women over 40 should consult their physician before starting any strength training program, especially concerning bone health and hormone considerations. Results depend on individual commitment, genetics, starting fitness level, and health status.
                    </p>
                </motion.div>
            </div>
        </section>
    )
}
